import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Save,
  Send,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Eye,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Info,
  CalendarClock,
  History as HistoryIcon,
  Settings2,
  ListChecks,
  LayoutDashboard,
  FlaskConical,
  ShieldCheck,
  CircleX,
  FileSpreadsheet,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Breadcrumb } from '../../components/Common'
import { EmailChipList } from './EmailNotifications'
import gstReminderService, { describeError } from '../../services/gstReminderService'
import '../../styles/email-notifications.css'
import '../../styles/gst-reminder-settings.css'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'UTC']
const MAX_STAGES = 16

const TRIGGER_OPTIONS = [
  { value: 'days_before', label: 'Days before due date' },
  { value: 'on_due_date', label: 'On the due date' },
  { value: 'days_after', label: 'Days after due date' },
  { value: 'specific_date', label: 'On a specific date' },
]

const CALC_OPTIONS = [
  { key: 'includeDraftSales', label: 'Include draft sales invoices', hint: 'Off: only approved / generated / sent / paid invoices count.' },
  { key: 'includeCancelled', label: 'Include cancelled invoices', hint: 'Cancelled sales and purchase invoices are normally left out.' },
  { key: 'includePurchasesMissingGst', label: 'Include purchases without supplier GSTIN', hint: 'Counted in purchase value, never in ITC.' },
  { key: 'requireVerificationForItc', label: 'Require ITC verification', hint: 'Only purchase invoices marked verified and eligible count as ITC; others show as pending.' },
  { key: 'itcOnlyEligible', label: 'Offset only eligible ITC in the payable estimate', hint: 'Off: pending ITC is also offset.' },
  { key: 'includeCreditNotes', label: 'Adjust credit notes', hint: 'No credit note documents exist in the ERP yet; noted in the summary.' },
  { key: 'includeDebitNotes', label: 'Adjust debit notes', hint: 'No debit note documents exist in the ERP yet; noted in the summary.' },
  { key: 'includeAdvances', label: 'Include advance receipts', hint: 'Advance receipts are not recorded separately in the ERP yet.' },
]

const STATUS_LABELS = { sent: 'Sent', failed: 'Failed', skipped: 'Skipped', pending: 'Pending' }
const TRIGGER_TYPE_LABELS = { scheduled: 'Scheduled', manual: 'Send now', test: 'Test', preview: 'Preview' }

function normalizeList(arr) {
  const seen = new Set()
  const out = []
  for (const raw of arr || []) {
    const e = String(raw || '').trim().toLowerCase()
    if (!EMAIL_RE.test(e) || seen.has(e)) continue
    seen.add(e)
    out.push(e)
  }
  return out
}

function inr(value, decimals = 2) {
  const n = Number(value) || 0
  return `${n < 0 ? '-' : ''}₹${Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

function todayIn(tz) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
  } catch (_) {
    return new Date().toISOString().slice(0, 10)
  }
}

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + n))
  return dt.toISOString().slice(0, 10)
}

function fmtDate(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return ''
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`
}

function fmtDateTime(value, tz) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return d.toLocaleString('en-IN', { timeZone: tz || undefined, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch (_) {
    return d.toLocaleString('en-IN')
  }
}

function describeTrigger(r) {
  const days = Number(r.daysOffset) || 0
  const plural = days === 1 ? 'day' : 'days'
  switch (r.triggerType) {
    case 'days_before': return `${days} ${plural} before due · ${r.sendTime}`
    case 'days_after': return `${days} ${plural} after due · ${r.sendTime}`
    case 'on_due_date': return `On due date · ${r.sendTime}`
    case 'specific_date': return `${fmtDate(r.specificDate) || 'Pick a date'} · ${r.sendTime}`
    default: return ''
  }
}

/** Next trigger date for a stage from the saved period due dates, so edits preview before saving. */
function clientNextTrigger(r, dueDates, today) {
  if (!r.enabled) return null
  if (r.triggerType === 'specific_date') return r.specificDate && r.specificDate >= today ? r.specificDate : null
  const offset = Number(r.daysOffset) || 0
  const candidates = dueDates.filter(Boolean).map((due) => {
    if (r.triggerType === 'days_before') return addDays(due, -offset)
    if (r.triggerType === 'days_after') return addDays(due, offset)
    return due
  })
  return candidates.filter((d) => d >= today).sort()[0] || null
}

function newStage(sortOrder) {
  return {
    _id: undefined,
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: 'New reminder',
    enabled: true,
    triggerType: 'days_before',
    daysOffset: 3,
    specificDate: '',
    sendTime: '10:00',
    timezone: '',
    toUserIds: [],
    ccUserIds: [],
    toEmails: [],
    ccEmails: [],
    subjectTemplate: '',
    includeSummary: true,
    includeAttachment: true,
    sendOnlyIfDataExists: false,
    sendOnlyIfPreviousIncomplete: false,
    sortOrder,
  }
}

function stageKey(stage) {
  return stage._id || stage.key
}

function comparable(settings) {
  if (!settings) return ''
  const { lastRun, updatedAt, _id, ...rest } = settings
  return JSON.stringify({
    ...rest,
    reminders: (rest.reminders || []).map(({ key, ...r }, i) => ({ ...r, sortOrder: i })),
  })
}

function reportError(err, fallback) {
  const { message, handled } = describeError(err, fallback)
  if (!handled) toast.error(message)
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function base64ToBlob(base64, contentType) {
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: contentType })
}

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label className={`en-toggle${checked ? ' is-on' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className={`en-switch${checked ? ' is-on' : ''}`} aria-hidden><i /></span>
      {label ? <span className="en-toggle__label">{label}</span> : null}
    </label>
  )
}

function CheckRow({ label, hint, checked, onChange, disabled }) {
  return (
    <label className={`gr-check${disabled ? ' is-disabled' : ''}`}>
      <input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="gr-check__copy">
        <strong>{label}</strong>
        {hint ? <em>{hint}</em> : null}
      </span>
    </label>
  )
}

function StatusBadge({ status }) {
  return <span className={`gr-badge gr-badge--${status || 'none'}`}>{STATUS_LABELS[status] || '—'}</span>
}

/** To / CC chips over team user ids + free-typed emails, as stored on a reminder stage. */
function StageRecipients({ label, hint, ids, emails, users, disabled, onChange }) {
  const userById = useMemo(() => new Map(users.map((u) => [String(u._id), u])), [users])
  const display = normalizeList([...ids.map((id) => userById.get(String(id))?.email), ...emails])
  const handleChange = (next) => {
    const list = normalizeList(next)
    const keptIds = ids.filter((id) => list.includes(userById.get(String(id))?.email))
    const idEmails = new Set(keptIds.map((id) => userById.get(String(id))?.email))
    onChange({ ids: keptIds, emails: list.filter((e) => !idEmails.has(e)) })
  }
  const pick = (u) => {
    const id = String(u._id)
    onChange({ ids: ids.includes(id) ? ids : [...ids, id], emails: emails.filter((e) => e !== u.email) })
  }
  return (
    <EmailChipList label={label} hint={hint} emails={display} onChange={handleChange} onPickUser={pick} disabled={disabled} users={users} />
  )
}

function StageEditor({ stage, users, disabled, onChange }) {
  const set = (patch) => onChange({ ...stage, ...patch })
  return (
    <div className="gr-stage-editor">
      <div className="gr-grid">
        <label className="gr-field gr-field--wide">
          <span>Stage name</span>
          <input className="en-input" value={stage.name} maxLength={80} disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="gr-field">
          <span>When</span>
          <select className="en-input" value={stage.triggerType} disabled={disabled} onChange={(e) => set({ triggerType: e.target.value })}>
            {TRIGGER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {(stage.triggerType === 'days_before' || stage.triggerType === 'days_after') && (
          <label className="gr-field gr-field--narrow">
            <span>Days</span>
            <input type="number" min={0} max={60} className="en-input" value={stage.daysOffset} disabled={disabled}
              onChange={(e) => set({ daysOffset: e.target.value === '' ? '' : Number(e.target.value) })} />
          </label>
        )}
        {stage.triggerType === 'specific_date' && (
          <label className="gr-field">
            <span>Date</span>
            <input type="date" className="en-input" value={stage.specificDate || ''} disabled={disabled} onChange={(e) => set({ specificDate: e.target.value })} />
          </label>
        )}
        <label className="gr-field gr-field--narrow">
          <span>Send time</span>
          <input type="time" className="en-input" value={stage.sendTime} disabled={disabled} onChange={(e) => set({ sendTime: e.target.value || '10:00' })} />
        </label>
        <label className="gr-field">
          <span>Timezone (optional)</span>
          <input className="en-input" list="gr-tz-list" value={stage.timezone || ''} placeholder="Company timezone" disabled={disabled}
            onChange={(e) => set({ timezone: e.target.value })} />
        </label>
        <label className="gr-field gr-field--full">
          <span>Subject</span>
          <input className="en-input" value={stage.subjectTemplate || ''} maxLength={200} disabled={disabled}
            placeholder="GST reminder: {{period}} due {{dueDate}} ({{reminderName}})"
            onChange={(e) => set({ subjectTemplate: e.target.value })} />
          <em className="gr-field__hint">
            Placeholders: {'{{period}} {{dueDate}} {{daysRemaining}} {{companyName}} {{gstin}} {{reminderName}} {{netPayable}} {{exceptionCount}}'}
          </em>
        </label>
      </div>

      <div className="gr-check-grid">
        <CheckRow label="Include summary in email" checked={stage.includeSummary} disabled={disabled} onChange={(v) => set({ includeSummary: v })} />
        <CheckRow label="Attach working statement" checked={stage.includeAttachment} disabled={disabled} onChange={(v) => set({ includeAttachment: v })} />
        <CheckRow label="Only if the period has invoices" checked={stage.sendOnlyIfDataExists} disabled={disabled} onChange={(v) => set({ sendOnlyIfDataExists: v })} />
        <CheckRow
          label="Only if data is still incomplete"
          hint="Skips this stage when there are no open exceptions and no ITC pending verification."
          checked={stage.sendOnlyIfPreviousIncomplete}
          disabled={disabled}
          onChange={(v) => set({ sendOnlyIfPreviousIncomplete: v })}
        />
      </div>

      <StageRecipients label="To" hint="Primary recipients" ids={stage.toUserIds} emails={stage.toEmails} users={users} disabled={disabled}
        onChange={({ ids, emails }) => set({ toUserIds: ids, toEmails: emails })} />
      <StageRecipients label="CC" hint="Copies" ids={stage.ccUserIds} emails={stage.ccEmails} users={users} disabled={disabled}
        onChange={({ ids, emails }) => set({ ccUserIds: ids, ccEmails: emails })} />
    </div>
  )
}

function Kpi({ label, value, tone, sub }) {
  return (
    <div className={`gr-kpi${tone ? ` gr-kpi--${tone}` : ''}`}>
      <span className="gr-kpi__label">{label}</span>
      <strong className="gr-kpi__value">{value}</strong>
      {sub ? <em className="gr-kpi__sub">{sub}</em> : null}
    </div>
  )
}

function PreviewPanel({ preview, canOperate, onReviewItc, reviewing, onDownload }) {
  const s = preview.summary
  const pay = s.estimatedPayable
  const pendingItc = s.purchases.invoices.filter((r) => r.itcStatus === 'pending')
  return (
    <section className="gr-card gr-preview">
      <div className="gr-card__head">
        <div>
          <p className="gr-eyebrow">Preview · {s.period.periodLabel}</p>
          <h3>Working summary</h3>
        </div>
        {preview.attachment ? (
          <button type="button" className="en-btn en-btn--secondary" onClick={onDownload}>
            <FileSpreadsheet size={16} aria-hidden />
            Download Excel
          </button>
        ) : null}
      </div>
      <p className="en-callout en-callout--warn">
        <AlertCircle size={14} aria-hidden />
        Draft working statement – subject to verification. Nothing is marked as filed.
      </p>

      <div className="gr-kpis">
        <Kpi label="Sales (taxable)" value={inr(s.sales.taxableValue)} sub={`${s.sales.count} invoices`} />
        <Kpi label="Purchases (taxable)" value={inr(s.purchases.taxableValue)} sub={`${s.purchases.count} invoices`} />
        <Kpi label="Output GST" value={inr(s.outputTax.total)} />
        <Kpi label="ITC eligible" value={inr(s.itc.eligible.total)} tone="good" sub={`${s.itc.eligible.count} invoices`} />
        <Kpi label="ITC pending" value={inr(s.itc.pending.total)} tone={s.itc.pending.count ? 'warn' : undefined} sub={`${s.itc.pending.count} to verify`} />
        <Kpi label="Est. net payable" value={inr(pay.total, 0)} tone="primary" sub={`Round-off: ${pay.roundOffMethod}`} />
        <Kpi label="Exceptions" value={s.exceptions.total} tone={s.exceptions.total ? 'warn' : 'good'} />
      </div>

      <table className="gr-table">
        <thead>
          <tr><th>Head</th><th className="n">Output tax</th><th className="n">ITC offset</th><th className="n">Payable</th><th className="n">Carry forward</th></tr>
        </thead>
        <tbody>
          {[['CGST', 'cgst'], ['SGST / UTGST', 'sgst'], ['IGST', 'igst']].map(([label, k]) => (
            <tr key={k}>
              <td>{label}</td>
              <td className="n">{inr(s.outputTax[k])}</td>
              <td className="n">{inr(s.itc.claimable[k])}</td>
              <td className="n">{inr(pay[k], 0)}</td>
              <td className="n">{inr(pay.carryForward[k])}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Total</td>
            <td className="n">{inr(s.outputTax.total)}</td>
            <td className="n">{inr(s.itc.claimable.total)}</td>
            <td className="n">{inr(pay.total, 0)}</td>
            <td className="n">{inr(pay.carryForward.total)}</td>
          </tr>
        </tbody>
      </table>

      {s.exceptions.total > 0 && (
        <>
          <h4 className="gr-subhead">Exceptions ({s.exceptions.total})</h4>
          <div className="gr-table-wrap">
            <table className="gr-table">
              <thead><tr><th>Side</th><th>Issue</th><th>Invoice</th><th>Date</th><th>Party</th><th>Detail</th></tr></thead>
              <tbody>
                {s.exceptions.items.slice(0, 50).map((e, i) => (
                  <tr key={`${e.invoiceNumber}-${e.type}-${i}`}>
                    <td>{e.source === 'sales' ? 'Sales' : 'Purchase'}</td>
                    <td>{e.label}</td>
                    <td>{e.invoiceNumber}</td>
                    <td>{fmtDate(e.invoiceDate)}</td>
                    <td>{e.party}</td>
                    <td className="gr-muted">{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {s.exceptions.total > 50 ? <p className="gr-muted">Showing 50 of {s.exceptions.total}. The Excel file lists all.</p> : null}
        </>
      )}

      {pendingItc.length > 0 && (
        <>
          <h4 className="gr-subhead">ITC awaiting verification ({pendingItc.length})</h4>
          <div className="gr-table-wrap">
            <table className="gr-table">
              <thead><tr><th>Invoice</th><th>Supplier</th><th>GSTIN</th><th className="n">Tax</th><th /></tr></thead>
              <tbody>
                {pendingItc.map((r) => (
                  <tr key={r.id}>
                    <td>{r.invoiceNumber}<span className="gr-muted"> · {r.supplierInvoiceNumber}</span></td>
                    <td>{r.party}</td>
                    <td>{r.gstin}</td>
                    <td className="n">{inr(r.tax)}</td>
                    <td className="gr-row-actions">
                      {r.reviewed ? (
                        <span className="gr-muted">{r.reviewed}</span>
                      ) : (
                        <>
                          <button type="button" className="gr-icon-btn" disabled={!canOperate || reviewing === r.id} onClick={() => onReviewItc(r, true)} title="Verified and eligible">
                            <ShieldCheck size={15} aria-hidden /> Eligible
                          </button>
                          <button type="button" className="gr-icon-btn gr-icon-btn--danger" disabled={!canOperate || reviewing === r.id} onClick={() => onReviewItc(r, false)} title="Verified, not eligible">
                            <CircleX size={15} aria-hidden /> Ineligible
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="gr-muted">Run the preview again after reviewing to refresh totals.</p>
        </>
      )}

      {s.notes.length > 0 && (
        <ul className="gr-notes">
          {s.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
    </section>
  )
}

const GstReminderSettings = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [canEdit, setCanEdit] = useState(false)
  const [canOperate, setCanOperate] = useState(false)
  const [users, setUsers] = useState([])
  const [saved, setSaved] = useState(null)
  const [form, setForm] = useState(null)
  const [status, setStatus] = useState(null)
  const [periodInfo, setPeriodInfo] = useState(null)
  const [saving, setSaving] = useState(false)
  const [expandedStage, setExpandedStage] = useState(null)

  const [periodChoice, setPeriodChoice] = useState('current')
  const [actionStageId, setActionStageId] = useState('')
  const [testEmail, setTestEmail] = useState('')
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [testing, setTesting] = useState(false)
  const [sending, setSending] = useState(false)
  const [reviewing, setReviewing] = useState(null)

  const [history, setHistory] = useState({ items: [], page: 1, pages: 1, total: 0 })
  const [historyFilter, setHistoryFilter] = useState({ status: '', includePreview: false })
  const [historyLoading, setHistoryLoading] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      const [statusRes, periodRes] = await Promise.all([gstReminderService.getStatus(), gstReminderService.getPeriod()])
      setStatus(statusRes?.data || null)
      setPeriodInfo(periodRes?.data || null)
    } catch (err) {
      reportError(err, 'Failed to load GST reminder status')
    }
  }, [])

  const loadHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true)
    try {
      const res = await gstReminderService.listHistory({
        page,
        limit: 20,
        ...(historyFilter.status ? { status: historyFilter.status } : {}),
        ...(historyFilter.includePreview ? { includePreview: 1 } : {}),
      })
      setHistory(res?.data || { items: [], page: 1, pages: 1, total: 0 })
    } catch (err) {
      reportError(err, 'Failed to load history')
    } finally {
      setHistoryLoading(false)
    }
  }, [historyFilter])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await gstReminderService.getSettings()
      const data = res?.data || {}
      setSaved(data.settings)
      setForm(data.settings)
      setUsers((data.users || []).filter((u) => EMAIL_RE.test(u.email || '')))
      setCanEdit(Boolean(data.canEdit))
      setCanOperate(Boolean(data.canOperate))
      await loadStatus()
    } catch (err) {
      reportError(err, 'Failed to load GST reminder settings')
    } finally {
      setLoading(false)
    }
  }, [loadStatus])

  useEffect(() => { load() }, [load])
  useEffect(() => { if (activeTab === 'history') loadHistory(1) }, [activeTab, loadHistory])

  useEffect(() => {
    if (!form?.reminders?.length) return
    if (!form.reminders.some((r) => r._id === actionStageId)) {
      setActionStageId(form.reminders.find((r) => r._id && r.enabled)?._id || form.reminders.find((r) => r._id)?._id || '')
    }
  }, [form, actionStageId])

  const isDirty = useMemo(() => comparable(form) !== comparable(saved), [form, saved])
  const generalDirty = useMemo(() => {
    if (!form || !saved) return false
    return ['filingFrequency', 'dueDay', 'timezone', 'weekendHolidayPolicy'].some((k) => String(form[k]) !== String(saved[k]))
  }, [form, saved])

  const today = todayIn(form?.timezone)
  const dueDates = [status?.period?.dueDateLocal, status?.nextPeriod?.dueDateLocal]

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const setCalc = (key, value) => setForm((f) => ({ ...f, calcOptions: { ...f.calcOptions, [key]: value } }))
  const setAttach = (key, value) => setForm((f) => ({ ...f, attachmentDefaults: { ...f.attachmentDefaults, [key]: value } }))

  const updateStage = (key, next) => setForm((f) => ({ ...f, reminders: f.reminders.map((r) => (stageKey(r) === key ? next : r)) }))
  const moveStage = (index, delta) => setForm((f) => {
    const list = [...f.reminders]
    const target = index + delta
    if (target < 0 || target >= list.length) return f
    ;[list[index], list[target]] = [list[target], list[index]]
    return { ...f, reminders: list.map((r, i) => ({ ...r, sortOrder: i })) }
  })
  const removeStage = (key) => {
    const stage = form.reminders.find((r) => stageKey(r) === key)
    if (!window.confirm(`Delete the "${stage?.name}" stage? Its history is kept.`)) return
    setForm((f) => ({ ...f, reminders: f.reminders.filter((r) => stageKey(r) !== key).map((r, i) => ({ ...r, sortOrder: i })) }))
  }
  const addStage = () => {
    const stage = newStage(form.reminders.length)
    setForm((f) => ({ ...f, reminders: [...f.reminders, stage] }))
    setExpandedStage(stageKey(stage))
  }

  const handleSave = async () => {
    if (!canEdit || !form) return
    setSaving(true)
    try {
      const payload = {
        ...form,
        reminders: form.reminders.map(({ key, ...r }, i) => ({ ...r, daysOffset: Number(r.daysOffset) || 0, sortOrder: i })),
      }
      const res = await gstReminderService.saveSettings(payload)
      setSaved(res.data)
      setForm(res.data)
      setExpandedStage(null)
      toast.success(res?.message || 'Settings saved')
      loadStatus()
    } catch (err) {
      reportError(err, 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const selectedPeriodKey = periodInfo?.[periodChoice]?.periodKey

  const handlePreview = async () => {
    setPreviewing(true)
    try {
      const res = await gstReminderService.preview({ periodKey: selectedPeriodKey })
      setPreview(res.data)
    } catch (err) {
      reportError(err, 'Failed to build preview')
    } finally {
      setPreviewing(false)
    }
  }

  const handleDownloadPreview = () => {
    const a = preview?.attachment
    if (!a) return
    downloadBlob(base64ToBlob(a.base64, a.contentType), a.fileName)
  }

  const handleReviewItc = async (row, eligible) => {
    let reason
    if (!eligible) {
      reason = window.prompt(`Why is ITC on ${row.invoiceNumber} not eligible?`, 'Blocked credit u/s 17(5)')
      if (reason === null) return
    }
    setReviewing(row.id)
    try {
      await gstReminderService.updateItcReview(row.id, eligible
        ? { itcEligible: true, itcVerified: true, itcIneligibleReason: '' }
        : { itcEligible: false, itcVerified: true, itcIneligibleReason: reason })
      setPreview((p) => ({
        ...p,
        summary: {
          ...p.summary,
          purchases: {
            ...p.summary.purchases,
            invoices: p.summary.purchases.invoices.map((r) => (r.id === row.id ? { ...r, reviewed: eligible ? 'Marked eligible' : 'Marked ineligible' } : r)),
          },
        },
      }))
    } catch (err) {
      reportError(err, 'Failed to save ITC review')
    } finally {
      setReviewing(null)
    }
  }

  const handleTest = async () => {
    if (!actionStageId) {
      toast.error('Save the stage first, then send a test')
      return
    }
    const email = testEmail.trim().toLowerCase()
    if (email && !EMAIL_RE.test(email)) {
      toast.error(`Invalid email: ${email}`)
      return
    }
    setTesting(true)
    try {
      const res = await gstReminderService.sendTest({
        reminderId: actionStageId,
        periodKey: selectedPeriodKey,
        ...(email ? { toEmails: [email] } : {}),
      })
      toast.success(res?.message || 'Test email sent')
      loadStatus()
    } catch (err) {
      reportError(err, 'Failed to send test email')
    } finally {
      setTesting(false)
    }
  }

  const handleSendNow = async () => {
    const stage = form.reminders.find((r) => r._id === actionStageId)
    if (!stage) {
      toast.error('Save the stage first')
      return
    }
    const periodLabel = periodInfo?.[periodChoice]?.periodLabel || ''
    if (!window.confirm(`Send "${stage.name}" for ${periodLabel} to its recipients now?`)) return
    setSending(true)
    const send = (resend) => gstReminderService.sendNow({ reminderId: stage._id, periodKey: selectedPeriodKey, resend })
    try {
      const res = await send(false)
      toast.success(res?.message || 'Reminder sent')
    } catch (err) {
      if (err?.response?.status === 409) {
        if (window.confirm(`${err.response.data?.message || 'Already sent.'}\n\nSend it again?`)) {
          try {
            const res = await send(true)
            toast.success(res?.message || 'Reminder re-sent')
          } catch (err2) {
            reportError(err2, 'Failed to resend reminder')
          }
        }
      } else {
        reportError(err, 'Failed to send reminder')
      }
    } finally {
      setSending(false)
      loadStatus()
    }
  }

  const handleDownloadHistory = async (row, index = 0) => {
    try {
      const res = await gstReminderService.downloadHistory(row._id, index)
      downloadBlob(res.data, row.attachments?.[index]?.fileName || 'gst-working-statement.xlsx')
    } catch (err) {
      reportError(err, 'Failed to download attachment')
    }
  }

  const mailChip = (() => {
    const m = status?.mail
    if (!m) return null
    const provider = m.provider === 'zoho_api' ? 'Zoho Mail' : 'SMTP'
    const ready = m.configured === true
    return (
      <span className={`en-status-chip${ready ? ' is-ready' : ' is-warn'}`} title={ready ? `Sending as ${m.fromEmail || 'configured sender'}` : (m.missing?.length ? `Missing: ${m.missing.join(', ')}` : m.authError || 'Mail not ready')}>
        {ready ? <CheckCircle2 size={14} aria-hidden /> : <AlertCircle size={14} aria-hidden />}
        <span>{ready ? `${provider} configured` : `${provider} not ready`}</span>
      </span>
    )
  })()

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'GST Reminder & Compliance', path: '/settings/gst-reminder-settings', isCurrent: true },
  ]

  if (loading || !form) {
    return (
      <div className="sales-dashboard en-page gr-page">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
        <p className="en-loading">Loading…</p>
      </div>
    )
  }

  const period = status?.period
  const savedStages = form.reminders.filter((r) => r._id)
  const statusById = new Map((status?.reminders || []).map((r) => [r._id, r]))

  const dashboardTab = (
    <div className="gr-dashboard">
      <header className="gr-hero">
        <div className="gr-hero__main">
          <p className="gr-eyebrow gr-eyebrow--light">Current return period · {form.filingFrequency}</p>
          <h2 className="gr-hero__period">{period?.periodLabel || '—'}</h2>
          <p className="gr-hero__due">
            Due {fmtDate(period?.dueDateLocal)}
            {period?.dueDateAdjusted ? <span className="gr-muted-light"> (moved from {fmtDate(period.calculatedDueDate)})</span> : null}
          </p>
        </div>
        <div className="gr-hero__stat">
          <strong>{period ? (period.daysRemaining < 0 ? `${Math.abs(period.daysRemaining)}d overdue` : period.daysRemaining === 0 ? 'Today' : `${period.daysRemaining} days`) : '—'}</strong>
          <em>{period && period.daysRemaining >= 0 ? 'until due date' : ''}</em>
        </div>
        <div className="gr-hero__stat">
          <strong>{form.enabled ? 'On' : 'Off'}</strong>
          <em>Scheduled reminders</em>
        </div>
      </header>

      {!saved.enabled && (
        <p className="en-callout en-callout--warn">
          <AlertCircle size={14} aria-hidden />
          Scheduled reminders are off. Turn them on under General &amp; calculation once recipients are set.
        </p>
      )}

      <div className="gr-dash-grid">
        <section className="gr-card">
          <div className="gr-card__head"><h3><CalendarClock size={16} aria-hidden /> Next reminder</h3></div>
          {status?.nextReminder ? (
            <div className="gr-next">
              <strong>{status.nextReminder.name}</strong>
              <span>{fmtDateTime(status.nextReminder.next.triggerAt, form.timezone)} · {status.nextReminder.next.periodLabel}</span>
              <span className="gr-muted">
                To: {status.nextReminder.recipients.to.join(', ') || 'nobody yet'}
                {status.nextReminder.recipients.cc.length ? ` · CC: ${status.nextReminder.recipients.cc.join(', ')}` : ''}
                {status.nextReminder.recipients.usedDefaults && status.nextReminder.recipients.to.length ? ' (company defaults)' : ''}
              </span>
            </div>
          ) : (
            <p className="gr-muted">{saved.enabled ? 'No enabled stage is scheduled.' : 'Scheduling is off.'}</p>
          )}
          {status?.lastRun ? (
            <p className="gr-last-run">
              Last run {fmtDateTime(status.lastRun.at, form.timezone)} · {status.lastRun.reminderName} · {status.lastRun.gstPeriodLabel} · <StatusBadge status={status.lastRun.status} />
              {status.lastRun.message ? <span className="gr-muted"> {status.lastRun.message}</span> : null}
            </p>
          ) : null}
        </section>

        <section className="gr-card">
          <div className="gr-card__head"><h3><Send size={16} aria-hidden /> Actions</h3></div>
          <div className="gr-actions-form">
            <label className="gr-field">
              <span>Period</span>
              <select className="en-input" value={periodChoice} onChange={(e) => setPeriodChoice(e.target.value)}>
                {['previous', 'current', 'next'].map((k) => periodInfo?.[k] ? (
                  <option key={k} value={k}>{periodInfo[k].periodLabel} — due {fmtDate(periodInfo[k].dueDateLocal)}{k === 'current' ? ' (current)' : ''}</option>
                ) : null)}
              </select>
            </label>
            <label className="gr-field">
              <span>Stage</span>
              <select className="en-input" value={actionStageId} onChange={(e) => setActionStageId(e.target.value)}>
                {savedStages.map((r) => <option key={r._id} value={r._id}>{r.name}{r.enabled ? '' : ' (off)'}</option>)}
              </select>
            </label>
            <label className="gr-field">
              <span>Test recipient (optional)</span>
              <input className="en-input" type="email" value={testEmail} placeholder="Defaults to the stage recipients" onChange={(e) => setTestEmail(e.target.value)} />
            </label>
          </div>
          <div className="gr-button-row">
            <button type="button" className="en-btn en-btn--primary" onClick={handlePreview} disabled={!canOperate || previewing}>
              <Eye size={16} aria-hidden /> {previewing ? 'Building…' : 'Preview'}
            </button>
            <button type="button" className="en-btn en-btn--secondary" onClick={handleTest} disabled={!canOperate || testing || !actionStageId}>
              <FlaskConical size={16} aria-hidden /> {testing ? 'Sending…' : 'Send test'}
            </button>
            <button type="button" className="en-btn en-btn--secondary" onClick={handleSendNow} disabled={!canOperate || sending || !actionStageId}>
              <Send size={16} aria-hidden /> {sending ? 'Sending…' : 'Send now'}
            </button>
          </div>
          {!canOperate ? <p className="gr-muted">Only finance users or administrators can preview or send.</p> : null}
        </section>
      </div>

      <section className="gr-card">
        <div className="gr-card__head">
          <h3><ListChecks size={16} aria-hidden /> Stages</h3>
          <button type="button" className="en-link-btn" onClick={loadStatus}><RefreshCw size={13} aria-hidden /> Refresh</button>
        </div>
        <div className="gr-table-wrap">
          <table className="gr-table">
            <thead><tr><th>Stage</th><th>Rule</th><th>Next send</th><th>Recipients</th><th>Last result</th></tr></thead>
            <tbody>
              {(status?.reminders || []).map((r) => (
                <tr key={r._id} className={r.enabled ? '' : 'is-off'}>
                  <td>{r.name}{r.enabled ? '' : <span className="gr-muted"> · off</span>}</td>
                  <td>{describeTrigger(r)}</td>
                  <td>{r.next ? `${fmtDateTime(r.next.triggerAt, form.timezone)} · ${r.next.periodLabel}` : '—'}</td>
                  <td>{r.recipients.to.length + r.recipients.cc.length || <span className="gr-warn-text">None</span>}{r.recipients.usedDefaults && r.recipients.to.length ? <span className="gr-muted"> (defaults)</span> : null}</td>
                  <td>
                    {r.last ? (
                      <>
                        <StatusBadge status={r.last.deliveryStatus} />
                        <span className="gr-muted"> {r.last.gstPeriodLabel} · {fmtDateTime(r.last.sentAt || r.last.createdAt, form.timezone)}</span>
                        {r.last.failureReason ? <div className="gr-muted gr-small">{r.last.failureReason}</div> : null}
                      </>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {preview ? (
        <PreviewPanel preview={preview} canOperate={canOperate} onReviewItc={handleReviewItc} reviewing={reviewing} onDownload={handleDownloadPreview} />
      ) : null}
    </div>
  )

  const generalTab = (
    <div className="gr-general">
      <section className="gr-card">
        <div className="gr-card__head">
          <h3><CalendarClock size={16} aria-hidden /> Filing schedule</h3>
          <Toggle checked={form.enabled} disabled={!canEdit} onChange={(v) => setField('enabled', v)} label={form.enabled ? 'Scheduled reminders on' : 'Scheduled reminders off'} />
        </div>
        <div className="gr-grid">
          <label className="gr-field">
            <span>Filing frequency</span>
            <select className="en-input" value={form.filingFrequency} disabled={!canEdit} onChange={(e) => setField('filingFrequency', e.target.value)}>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
            </select>
          </label>
          <label className="gr-field gr-field--narrow">
            <span>Due day</span>
            <input type="number" min={1} max={31} className="en-input" value={form.dueDay} disabled={!canEdit}
              onChange={(e) => setField('dueDay', e.target.value === '' ? '' : Number(e.target.value))} />
          </label>
          <label className="gr-field">
            <span>Timezone</span>
            <input className="en-input" list="gr-tz-list" value={form.timezone} disabled={!canEdit} onChange={(e) => setField('timezone', e.target.value)} />
          </label>
          <label className="gr-field">
            <span>Weekend due date</span>
            <select className="en-input" value={form.weekendHolidayPolicy} disabled={!canEdit} onChange={(e) => setField('weekendHolidayPolicy', e.target.value)}>
              <option value="calculated">Keep calculated date</option>
              <option value="previous_working">Move to previous working day</option>
              <option value="next_working">Move to next working day</option>
            </select>
          </label>
        </div>
        <p className="en-callout en-callout--muted">
          <Info size={14} aria-hidden />
          Due date is day {form.dueDay || '—'} of the month after the {form.filingFrequency === 'Quarterly' ? 'quarter' : 'month'} ends (clamped to month length). Only Saturdays and Sundays are treated as non-working days.
        </p>
        <CheckRow label="Skip scheduled reminders when the period has no invoices" checked={form.sendOnlyWhenDataExists} disabled={!canEdit} onChange={(v) => setField('sendOnlyWhenDataExists', v)} />
      </section>

      <section className="gr-card">
        <div className="gr-card__head"><h3><Settings2 size={16} aria-hidden /> Calculation</h3></div>
        <div className="gr-check-grid">
          {CALC_OPTIONS.map((o) => (
            <CheckRow key={o.key} label={o.label} hint={o.hint} checked={form.calcOptions[o.key]} disabled={!canEdit} onChange={(v) => setCalc(o.key, v)} />
          ))}
        </div>
        <label className="gr-field gr-field--inline">
          <span>Round payable per head</span>
          <select className="en-input" value={form.calcOptions.roundOffMethod} disabled={!canEdit} onChange={(e) => setCalc('roundOffMethod', e.target.value)}>
            <option value="nearest">To nearest rupee</option>
            <option value="floor">Down</option>
            <option value="ceil">Up</option>
          </select>
        </label>
      </section>

      <section className="gr-card">
        <div className="gr-card__head"><h3><FileSpreadsheet size={16} aria-hidden /> Attachment</h3></div>
        <div className="gr-check-grid">
          <CheckRow label="Attach working statement" hint="Per-stage switch still applies." checked={form.attachmentDefaults.attachWorkingStatement} disabled={!canEdit} onChange={(v) => setAttach('attachWorkingStatement', v)} />
          <CheckRow label="Include invoice-level detail" hint="Sales, Purchases and ITC sheets list each invoice." checked={form.attachmentDefaults.includeInvoiceDetails} disabled={!canEdit} onChange={(v) => setAttach('includeInvoiceDetails', v)} />
        </div>
        <label className="gr-field gr-field--inline">
          <span>Format</span>
          <select className="en-input" value={form.attachmentDefaults.attachmentFormat} disabled={!canEdit} onChange={(e) => setAttach('attachmentFormat', e.target.value)}>
            <option value="excel">Excel</option>
            <option value="pdf">Printable statement (save as PDF)</option>
            <option value="both">Both</option>
          </select>
        </label>
      </section>

      <section className="gr-card">
        <div className="gr-card__head"><h3><Send size={16} aria-hidden /> Default recipients</h3></div>
        <p className="gr-muted">Used only for stages that have no To or CC of their own.</p>
        <EmailChipList label="To" hint="Fallback primary" emails={form.defaultToEmails} users={users} disabled={!canEdit}
          onChange={(list) => setField('defaultToEmails', normalizeList(list))}
          onPickUser={(u) => setField('defaultToEmails', normalizeList([...form.defaultToEmails, u.email]))} />
        <EmailChipList label="CC" hint="Fallback copies" emails={form.defaultCcEmails} users={users} disabled={!canEdit}
          onChange={(list) => setField('defaultCcEmails', normalizeList(list))}
          onPickUser={(u) => setField('defaultCcEmails', normalizeList([...form.defaultCcEmails, u.email]))} />
      </section>
    </div>
  )

  const stagesTab = (
    <div className="gr-stages">
      {generalDirty ? (
        <p className="en-callout en-callout--warn"><AlertCircle size={14} aria-hidden /> Save the schedule changes to refresh next send dates.</p>
      ) : null}
      {form.reminders.length === 0 ? <p className="gr-muted">No stages yet.</p> : null}
      {form.reminders.map((r, index) => {
        const key = stageKey(r)
        const open = expandedStage === key
        const next = clientNextTrigger(r, dueDates, today)
        const recipients = r.toUserIds.length + r.toEmails.length + r.ccUserIds.length + r.ccEmails.length
        const last = r._id ? statusById.get(r._id)?.last : null
        return (
          <div key={key} className={`gr-stage${r.enabled ? '' : ' is-off'}${open ? ' is-open' : ''}`}>
            <div className="gr-stage__row">
              <div className="gr-stage__order">
                <button type="button" className="gr-icon-btn" disabled={!canEdit || index === 0} onClick={() => moveStage(index, -1)} aria-label="Move up"><ChevronUp size={15} /></button>
                <button type="button" className="gr-icon-btn" disabled={!canEdit || index === form.reminders.length - 1} onClick={() => moveStage(index, 1)} aria-label="Move down"><ChevronDown size={15} /></button>
              </div>
              <button type="button" className="gr-stage__summary" onClick={() => setExpandedStage(open ? null : key)}>
                <strong>{r.name || 'Untitled stage'}</strong>
                <span>{describeTrigger(r)}</span>
                <span className="gr-muted">
                  {r.enabled ? (next ? `Next: ${fmtDate(next)}` : 'No upcoming date') : 'Off'}
                  {' · '}
                  {recipients ? `${recipients} recipient${recipients === 1 ? '' : 's'}` : (form.defaultToEmails.length ? 'Company defaults' : 'No recipients')}
                  {last ? <> · <StatusBadge status={last.deliveryStatus} /></> : null}
                </span>
              </button>
              <div className="gr-stage__controls">
                <Toggle checked={r.enabled} disabled={!canEdit} onChange={(v) => updateStage(key, { ...r, enabled: v })} />
                <button type="button" className="gr-icon-btn gr-icon-btn--danger" disabled={!canEdit} onClick={() => removeStage(key)} aria-label="Delete stage"><Trash2 size={15} /></button>
              </div>
            </div>
            {open ? <StageEditor stage={r} users={users} disabled={!canEdit} onChange={(next2) => updateStage(key, next2)} /> : null}
          </div>
        )
      })}
      <button type="button" className="en-btn en-btn--secondary" onClick={addStage} disabled={!canEdit || form.reminders.length >= MAX_STAGES}>
        <Plus size={16} aria-hidden /> Add stage
      </button>
    </div>
  )

  const historyTab = (
    <div className="gr-history">
      <div className="gr-history__filters">
        <label className="gr-field gr-field--inline">
          <span>Status</span>
          <select className="en-input" value={historyFilter.status} onChange={(e) => setHistoryFilter((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <CheckRow label="Show previews" checked={historyFilter.includePreview} onChange={(v) => setHistoryFilter((f) => ({ ...f, includePreview: v }))} />
        <button type="button" className="en-link-btn" onClick={() => loadHistory(history.page)}><RefreshCw size={13} aria-hidden /> Refresh</button>
      </div>
      <div className="gr-table-wrap">
        <table className="gr-table">
          <thead>
            <tr><th>When</th><th>Period</th><th>Stage</th><th>Type</th><th>Status</th><th>Recipients</th><th className="n">Net payable</th><th className="n">Exceptions</th><th>File</th></tr>
          </thead>
          <tbody>
            {historyLoading ? (
              <tr><td colSpan={9} className="gr-muted">Loading…</td></tr>
            ) : history.items.length === 0 ? (
              <tr><td colSpan={9} className="gr-muted">No history yet.</td></tr>
            ) : history.items.map((h) => (
              <tr key={h._id}>
                <td>{fmtDateTime(h.sentAt || h.createdAt, form.timezone)}{h.triggeredBy?.name ? <div className="gr-muted gr-small">{h.triggeredBy.name}</div> : null}</td>
                <td>{h.gstPeriodLabel}</td>
                <td>{h.reminderName}{h.resendOf ? <span className="gr-muted"> · resend</span> : null}</td>
                <td>{TRIGGER_TYPE_LABELS[h.triggerType] || h.triggerType}</td>
                <td><StatusBadge status={h.deliveryStatus} />{h.failureReason ? <div className="gr-muted gr-small">{h.failureReason}</div> : null}</td>
                <td className="gr-small">{[...h.recipients.to, ...h.recipients.cc].join(', ') || '—'}</td>
                <td className="n">{h.generatedAt ? inr(h.summarySnapshot.netPayable, 0) : '—'}</td>
                <td className="n">{h.generatedAt ? h.summarySnapshot.exceptionCount : '—'}</td>
                <td>
                  {h.attachments.length ? h.attachments.map((a) => (
                    <button key={a.index} type="button" className="gr-icon-btn" disabled={!canOperate} onClick={() => handleDownloadHistory(h, a.index)} title={a.fileName}>
                      <Download size={14} aria-hidden /> {a.fileName.endsWith('.html') ? 'HTML' : 'Excel'}
                    </button>
                  )) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {history.pages > 1 ? (
        <div className="gr-pager">
          <button type="button" className="en-btn en-btn--secondary" disabled={history.page <= 1} onClick={() => loadHistory(history.page - 1)}>Previous</button>
          <span className="gr-muted">Page {history.page} of {history.pages} · {history.total} entries</span>
          <button type="button" className="en-btn en-btn--secondary" disabled={history.page >= history.pages} onClick={() => loadHistory(history.page + 1)}>Next</button>
        </div>
      ) : null}
    </div>
  )

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'general', label: 'General & calculation', icon: Settings2 },
    { id: 'stages', label: 'Reminder stages', icon: ListChecks },
    { id: 'history', label: 'History', icon: HistoryIcon },
  ]
  const showSaveBar = canEdit && (activeTab === 'general' || activeTab === 'stages')

  return (
    <div className="sales-dashboard en-page gr-page">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>GST Reminder &amp; Compliance</h1>
          <p>Reminders before each GST due date, with a working summary of sales, purchases, ITC and estimated payable</p>
        </div>
      </div>

      {!canEdit && (
        <div className="en-alert" role="status">
          <Info size={15} aria-hidden />
          {canOperate ? 'Administrators can change these settings. You can preview and send.' : 'View only. Administrators can change these settings.'}
        </div>
      )}

      <div className="en-toolbar">
        <div className="en-tabs" role="tablist" aria-label="GST reminder sections">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" role="tab" aria-selected={activeTab === id}
              className={`en-tabs__btn${activeTab === id ? ' is-active' : ''}`} onClick={() => setActiveTab(id)}>
              <Icon size={15} strokeWidth={1.75} aria-hidden />
              {label}
            </button>
          ))}
        </div>
        {mailChip}
      </div>

      <datalist id="gr-tz-list">
        {TIMEZONES.map((tz) => <option key={tz} value={tz} />)}
      </datalist>

      {activeTab === 'dashboard' && dashboardTab}
      {activeTab === 'general' && generalTab}
      {activeTab === 'stages' && stagesTab}
      {activeTab === 'history' && historyTab}

      {showSaveBar ? (
        <div className="gr-savebar">
          {isDirty ? <span className="en-actions__dirty">Unsaved changes</span> : <span className="gr-muted">{saved.updatedAt ? `Saved ${fmtDateTime(saved.updatedAt, form.timezone)}` : ''}</span>}
          <button type="button" className="en-btn en-btn--secondary" disabled={!isDirty || saving} onClick={() => setForm(saved)}>Discard</button>
          <button type="button" className="en-btn en-btn--primary" disabled={!isDirty || saving} onClick={handleSave}>
            <Save size={16} aria-hidden /> {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default GstReminderSettings
